import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState, TableSkeleton } from "@/components/common/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Bell, Plus, AlertTriangle, CheckCircle2, Info } from "lucide-react";

const icons = { warning: AlertTriangle, success: CheckCircle2, info: Info };
const colors = { warning: "text-[hsl(var(--warning))] bg-[hsl(var(--warning))]/10", success: "text-[hsl(var(--success))] bg-[hsl(var(--success))]/10", info: "text-primary bg-primary/10" };

export default function NotificationsPage() {
  const { t, can } = useApp();
  const [notifs, setNotifs] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState({ title: "", message: "", type: "info", scope: "organization", branch_id: null });
  const [saving, setSaving] = useState(false);

  const load = async () => { try { const { data } = await api.get("/notifications"); setNotifs(data); } catch { setNotifs([]); } };
  useEffect(() => { load(); if (can("notifications.manage")) api.get("/branches").then((r) => setBranches(r.data)).catch(() => {}); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, branch_id: form.scope === "branch" ? form.branch_id : null };
      await api.post("/notifications", payload); toast.success("Notification created"); setDialog(false);
      setForm({ title: "", message: "", type: "info", scope: "organization", branch_id: null }); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader title={t("notifications")} subtitle="Organization and branch announcements">
        {can("notifications.manage") && <Button size="sm" className="gap-2" onClick={() => setDialog(true)} data-testid="create-notif-btn"><Plus className="h-4 w-4" />New Notification</Button>}
      </PageHeader>

      {notifs === null ? <TableSkeleton cols={1} /> : notifs.length === 0 ? (
        <div className="bg-card border border-border rounded-md"><EmptyState icon={Bell} title={t("noNotifications")} /></div>
      ) : (
        <div className="space-y-3">
          {notifs.map((n) => {
            const Icon = icons[n.type] || Info;
            return (
              <div key={n.id} className="bg-card border border-border rounded-md p-4 flex items-start gap-3" data-testid={`notif-${n.id}`}>
                <div className={cn("h-9 w-9 rounded-md flex items-center justify-center shrink-0", colors[n.type] || colors.info)}><Icon className="h-4 w-4" /></div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm">{n.title}</p>
                  <p className="text-sm text-muted-foreground mt-0.5">{n.message}</p>
                  <p className="text-xs text-muted-foreground mt-1">{n.scope === "organization" ? "Organization-wide" : "Branch"} · {new Date(n.created_at).toLocaleString()}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Notification</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" data-testid="notif-title-input" /></div>
            <div><Label>Message *</Label><Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5" data-testid="notif-message-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="notif-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="info">Info</SelectItem><SelectItem value="success">Success</SelectItem><SelectItem value="warning">Warning</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Scope</Label>
                <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="notif-scope-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="organization">Organization</SelectItem><SelectItem value="branch">Branch</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            {form.scope === "branch" && (
              <div><Label>Branch</Label>
                <Select value={form.branch_id || ""} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="notif-branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
                  <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>{t("cancel")}</Button>
            <Button onClick={save} disabled={saving || !form.title || !form.message} data-testid="notif-save-btn">{t("create")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
